import React from 'react';
import { inject, observer } from 'mobx-react';
import Collection from 'core/model/Collection';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogFooter,
  Flex,
  Heading,
  Icon,
  Link,
  Spinner,
  Tag,
  Text,
  showErrorToast
} from 'core/components';
import { Table } from 'core/components/table';
import { CLOUD_PROVIDERS } from 'app/util/constants';

@inject('$clouds', '$auth')
@observer
export default class ConfigStatusOverview extends React.Component {
  static defaultProps = {
    showTitle: false
  };

  state = {
    loading: true,
    summary: null,
    showSummaryModal: false
  };

  collection = null;

  componentDidMount() {
    this.loadStatus();
  }

  loadStatus() {
    const { $auth } = this.props;

    // Check if user has permission to use new API status feature
    if ($auth.hasPermission('cloud.write_api_status_to_pg', { overrideForSudo: false })) {
      // Initialize collection without grouping for new feature
      if (!this.collection || this.collection.groupBy) {
        this.collection = new Collection([]);
      }
      this.loadCloudApiStatus();
    } else {
      // Initialize collection with grouping for original feature
      if (!this.collection || !this.collection.groupBy) {
        this.collection = new Collection([], { groupBy: 'cloudProvider' });
      }
      this.loadCloudConfigStatus();
    }
  }

  loadCloudApiStatus() {
    const { $clouds } = this.props;
    this.setState({ loading: true });

    $clouds
      .getCloudApiStatusSummary()
      .then((result) => {
        const { summary, stats } = result;
        const summaryData = {};

        // Initialize with all configured providers
        ($clouds.configStatusCloudProviders || []).forEach((provider) => {
          summaryData[provider] = {
            cloudProvider: provider,
            errorCount: 0,
            link: `/v4/settings/cloud-config-v2/${provider}`
          };
        });

        if (stats) {
          Object.keys(stats).forEach((provider) => {
            if (summaryData[provider]) {
              summaryData[provider].errorCount = stats[provider].error || 0;
              summaryData[provider].successCount = stats[provider].success || 0;
              summaryData[provider].totalCount = stats[provider].total || 0;
            }
          });
        }

        this.collection.processData(Object.values(summaryData));
        this.setState({ summary, loading: false });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load cloud status summary:', err);
        this.setState({ loading: false });
      });
  }

  loadCloudConfigStatus({ force = false } = {}) {
    const { $clouds } = this.props;

    this.setState({ loading: true }, () => {
      Promise.all(
        $clouds.configStatusCloudProviders.map((cloudProvider) =>
          $clouds.getCloudConfigStatus({ cloudProvider, force }).then(({ statusData }) => ({
            cloudProvider,
            data: statusData
          }))
        )
      )
        .then((results) => {
          const data = results.flatMap((r) => this.processStatusData(r));
          this.collection.processData(data);
        })
        .catch((e) => {
          showErrorToast('Error occurred attempting to process the cloud config status results');
          // eslint-disable-next-line no-console
          console.error(e);
        })
        .finally(() => this.setState({ loading: false }));
    });
  }

  handleRefreshConfigStatus = () => {
    this.loadStatus();
  };

  getExportStatus(exports) {
    return exports.reduce(
      (acc, model) => {
        if (model.get('enabled') && model.get('properties.task_status') === 'ERROR') {
          acc.error += 1;
        }

        return acc;
      },
      { error: 0 }
    );
  }

  processStatusData({ cloudProvider, data }) {
    if (!data || data.length === 0) {
      return [];
    }

    const statusData = {};
    data.forEach((acct) => {
      const { apiStatus, flowStatus, region, accountId, exports } = acct;

      if (!statusData[region]) {
        statusData[region] = {
          api: { success: 0, warning: 0, error: 0, critical: 0 },
          flow: { success: 0, error: 0, none: 0 },
          accountId,
          cloudExport: this.getExportStatus(exports),
          total: 0,
          region,
          cloudProvider
        };
      }

      statusData[region].api[apiStatus.toLowerCase()] += 1;
      statusData[region].flow[flowStatus.toLowerCase()] += 1;
      statusData[region].total += 1;
    });

    return Object.values(statusData);
  }

  get columns() {
    const { $auth } = this.props;

    // If user has permission to use new API status feature
    if ($auth.hasPermission('cloud.write_api_status_to_pg', { overrideForSudo: false })) {
      return [
        {
          name: 'cloudProvider',
          label: 'Provider',
          width: 120,
          renderer: ({ model }) => {
            const provider = model.get('cloudProvider');
            const { logo, code } = CLOUD_PROVIDERS.byId(provider);
            return (
              <Flex alignItems="center">
                {logo}
                <Text pl={1}>{code}</Text>
              </Flex>
            );
          }
        },
        {
          name: 'successes',
          label: 'Success',
          renderer: ({ model }) => <Text>{model.get('successCount')}</Text>
        },
        {
          name: 'errors',
          label: 'Errors',
          width: 100,
          renderer: ({ model }) => {
            const count = model.get('errorCount');
            return <Tag intent={count === 0 ? 'success' : 'danger'}>{count}</Tag>;
          }
        },
        {
          name: 'total',
          label: 'Total',
          renderer: ({ model }) => <Text>{model.get('totalCount')}</Text>
        },
        {
          name: 'action',
          label: 'Action',
          align: 'right',
          renderer: ({ model }) => (
            <Link
              to={model.get('link')}
              state={{
                stats: {
                  success: model.get('successCount'),
                  error: model.get('errorCount'),
                  total: model.get('totalCount')
                }
              }}
            >
              View Details
            </Link>
          )
        }
      ];
    }

    // Original columns for users without the permission
    return [
      {
        name: 'region',
        label: 'Region',
        width: 100,
        renderer: ({ model }) => (
          <Flex flexDirection="column">
            <Text>{model.get('region')}</Text>
            <Text small muted fontSize="10px">
              {model.get('accountId')}
            </Text>
          </Flex>
        )
      },

      /*
        Creates a list of tags to indicate api and flow status.
        When there are API severity of type 'warning' or 'error', the label will be pluralized if the count requires, the rest are left as-is.
        For example, we could have "2 api warnings" and "5 api critical"
      */
      {
        name: 'status',
        label: 'Status',
        wrapText: true,
        renderer: ({ model }) => {
          const api = model.get('api');
          const flow = model.get('flow');
          const cloudExport = model.get('cloudExport');
          const total = model.get('total');

          const tagProps = { m: '4px', minimal: true };
          const tags = [];

          if (api.success === total && flow.success + flow.none === total && cloudExport.error === 0) {
            return (
              <Tag intent="success" {...tagProps}>
                No Issues
              </Tag>
            );
          }

          // fill in any api problem tags
          ['warning', 'error', 'critical'].forEach((severity) => {
            const itemCount = api[severity];

            if (itemCount > 0) {
              const intent = severity === 'warning' ? 'warning' : 'danger';
              let label = `${api[severity]} api ${severity}`;

              if (severity === 'warning' || severity === 'error') {
                // pluralize if need be
                label = `${label}${api[severity] === 1 ? '' : 's'}`;
              }

              tags.push(
                <Tag key={`api-${severity}`} intent={intent} {...tagProps}>
                  {label}
                </Tag>
              );
            }
          });

          // check for flow errors
          if (flow.error > 0) {
            tags.push(<Tag key="flow-error" intent="danger" {...tagProps}>{`${flow.error} missing flow`}</Tag>);
          }

          // check for export errors
          if (cloudExport.error > 0) {
            tags.push(
              <Tag key="cloud-export-error" intent="danger" {...tagProps}>{`${cloudExport.error} export error${
                cloudExport.error > 1 ? 's' : ''
              }`}</Tag>
            );
          }

          return tags;
        }
      }
    ];
  }

  // Returns either a success tick icon for error-free conditions, or a tag with the rolled up count of issues, colored by severity
  getIssueRollup(group = []) {
    let count = 0;
    let intent = 'warning';

    group.forEach((model) => {
      const { critical, error, warning } = model.get('api');
      const { error: flowErrors } = model.get('flow');
      const { error: exportErrors } = model.get('cloudExport');
      const majorErrors = critical + error + flowErrors + exportErrors;

      count += majorErrors + warning;

      // if we have errors above warning level, bump up the intent
      if (majorErrors > 0) {
        intent = 'danger';
      }
    });

    if (count > 0) {
      return (
        <Tag intent={intent} round>
          {count}
        </Tag>
      );
    }

    return <Icon icon="tick-circle" intent="success" />;
  }

  groupSummary = ({ groupKey, group }) => {
    const { logo, code } = CLOUD_PROVIDERS.byId(groupKey);
    const label = groupKey !== 'aws' ? code : null; // the aws logo already says 'aws'

    return (
      <Flex alignItems="center" justifyContent="space-between" position="sticky" top={0} left={0}>
        <Flex alignItems="center">
          {logo}
          <Text pl={1}>{label}</Text>
          <Box pl={1}>{this.getIssueRollup(group)}</Box>
        </Flex>
        <Link to={`/v4/settings/cloud-config/${groupKey}`}>View Details</Link>
      </Flex>
    );
  };

  render() {
    const { $clouds, $auth, showTitle } = this.props;
    const { loading, summary, showSummaryModal } = this.state;

    // Determine which title and layout to use based on permission
    const hasApiStatusPgPerms = $auth.hasPermission('cloud.write_api_status_to_pg', { overrideForSudo: false });
    const title = hasApiStatusPgPerms ? 'Cloud Metadata Api Status' : 'Cloud Config Status';

    return (
      <Card display="flex" flexDirection="column" overflow="hidden" {...this.props}>
        {showTitle && (
          <Flex justifyContent="space-between" borderBottom="thin" alignItems="center">
            <Heading m={1} level={5} fontWeight="heavy">
              {title}
            </Heading>
            {hasApiStatusPgPerms && summary && (
              <Button small minimal onClick={() => this.setState({ showSummaryModal: true })}>
                View AI Summary
              </Button>
            )}
            {!hasApiStatusPgPerms && (
              <Button
                icon="refresh"
                intent="success"
                onClick={this.handleRefreshConfigStatus}
                disabled={loading}
                minimal
              />
            )}
          </Flex>
        )}
        {loading && (
          <Flex flexDirection="column" flex={1} alignItems="center" justifyContent="center">
            <Spinner size={24} />
            <Text pt={1} muted>
              {hasApiStatusPgPerms ? 'Checking API status...' : 'Checking APIs... This may take a minute...'}
            </Text>
          </Flex>
        )}

        {hasApiStatusPgPerms && (
          <Dialog
            isOpen={showSummaryModal}
            onClose={() => this.setState({ showSummaryModal: false })}
            title="Cloud Metadata API Status Summary"
            style={{ width: '90vw', maxWidth: '1000px' }}
          >
            <Box p={3} style={{ overflow: 'auto', maxHeight: '60vh' }}>
              {summary && <ReactMarkdown>{summary}</ReactMarkdown>}
            </Box>
            <DialogFooter>
              <Button onClick={() => this.setState({ showSummaryModal: false })}>Close</Button>
            </DialogFooter>
          </Dialog>
        )}

        {!loading && (
          <Table
            collection={this.collection}
            columns={this.columns}
            groupSummaryLookup={hasApiStatusPgPerms ? undefined : this.groupSummary}
            selectOnRowClick={false}
            hideHeader={!hasApiStatusPgPerms}
            style={{ overflow: 'auto' }}
            css={
              hasApiStatusPgPerms
                ? undefined
                : {
                    '.group-summary-wrapper': { flex: 1 }
                  }
            }
            isCollapsed={hasApiStatusPgPerms ? false : $clouds.configStatusCloudProviders.length > 1}
          />
        )}
      </Card>
    );
  }
}
